'use client'

import { useRef, useState } from 'react'

import { Star } from 'lucide-react'

import type { SimplifiedReview } from '@/lib/c2c/types'
import { Link } from '@/i18n/navigation'

// How many reviews are VISIBLE while the section is collapsed. Every review in the
// supplied set is rendered into the HTML regardless — the overflow is hidden, never
// omitted — so this bounds what a reader sees, not what a crawler reads. Raising it
// would add nothing to the page's indexable review text; only a larger supplied set
// does that. See
// .docs/plans/c2c-carrier-reviews/2026-08-19-c2c-crawlable-review-text.md.
const INITIALLY_VISIBLE_REVIEWS = 5

interface CarrierReviewsProps {
    title?: string
    subtitle?: string
    averageRating?: number
    reviews: SimplifiedReview[]
}

function StarRating({ rating }: { rating: number }) {
    return (
        <span className="mr-2 flex">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    className={`h-5 w-5 ${
                        i < rating ? 'fill-[#e9c530] text-[#e9c530]' : 'text-gray-300'
                    }`}
                />
            ))}
        </span>
    )
}

function ReviewCard({
    review,
    hidden,
    isFirst,
    isLast,
}: {
    review: SimplifiedReview
    hidden: boolean
    isFirst: boolean
    isLast: boolean
}) {
    // Pre-formatted on the server in the trip's origin TZ; see
    // src/lib/c2c/transforms/reviews.ts:formatReviewDate.
    const dateStr = review.displayDate
    const travelerSuffix = review.travelerType ? ` - ${review.travelerType}` : ''

    return (
        <div
            // `hidden` (display:none) rather than conditional rendering: the review text
            // stays in the server-rendered HTML for crawlers while leaving both the visual
            // layout and the accessibility tree, so a reader sees only the visible set.
            hidden={hidden}
            className={[
                'border-x border-t border-[#d9dee5] px-4 py-4 pb-6 md:p-6',
                isFirst && 'rounded-t-[7px]',
                isLast && 'rounded-b-[7px] border-b',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {review.rating !== null && (
                <div className="flex items-center pb-4">
                    <StarRating rating={review.rating} />
                    <p className="text-base text-[#44474a]">{review.rating.toFixed(1)}</p>
                </div>
            )}
            <div className="flex flex-col md:flex-row">
                {review.reviewerName && (
                    <span className="text-base font-bold text-[#44474a]">
                        {review.reviewerName}
                    </span>
                )}
                {dateStr && (
                    <p className="text-base text-[#44474a] md:mx-2">
                        {dateStr}
                        {travelerSuffix}
                    </p>
                )}
                {review.carrierName &&
                    (review.carrierGuideSlug ? (
                        <Link
                            href={`/carrier/${review.carrierGuideSlug}/`}
                            className="text-base font-semibold text-[#00bfd5] hover:text-[#008a99] hover:underline"
                        >
                            {review.carrierName}
                        </Link>
                    ) : (
                        <span className="text-base text-[#44474a]">{review.carrierName}</span>
                    ))}
            </div>
            {review.text && (
                <div className="mt-6">
                    <div className="text-base leading-6 text-[#44474a]">{review.text}</div>
                </div>
            )}
        </div>
    )
}

/**
 * Displays carrier reviews for a C2C route.
 * Returns null when no reviews are available.
 * Renders EVERY supplied review into the markup, showing the first
 * INITIALLY_VISIBLE_REVIEWS and hiding the rest behind a "Show More Reviews" button.
 */
export function CarrierReviews({
    title = 'Customer Reviews',
    subtitle,
    averageRating,
    reviews,
}: CarrierReviewsProps) {
    const [expanded, setExpanded] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    if (reviews.length === 0) {
        return null
    }

    // Count of reviews shown, NOT a slice of what is rendered — every review is
    // rendered either way; those at or past this index ship hidden.
    const visibleCount = expanded
        ? reviews.length
        : Math.min(INITIALLY_VISIBLE_REVIEWS, reviews.length)
    const hasMore = reviews.length > INITIALLY_VISIBLE_REVIEWS

    return (
        <section data-section="reviews" className="my-[72px] md:my-24">
            <h3 className="pb-4 text-2xl font-bold text-[#44474a]">{title}</h3>

            {subtitle && <p className="text-base text-[#44474a]">{subtitle}</p>}

            {averageRating != null && (
                <div className="flex items-center py-4 pb-8">
                    <span className="mr-2 flex">
                        <Star className="h-5 w-5 fill-[#e9c530] text-[#e9c530]" />
                    </span>
                    <span className="text-2xl font-bold text-[#44474a]">
                        {averageRating.toFixed(1)}
                    </span>
                </div>
            )}

            <div>
                {reviews.map((review, index) => (
                    <ReviewCard
                        key={review.id}
                        review={review}
                        hidden={index >= visibleCount}
                        isFirst={index === 0}
                        // The rounded/closed bottom edge belongs to the last VISIBLE
                        // card, so the collapsed section still reads as a closed list.
                        isLast={index === visibleCount - 1}
                    />
                ))}
            </div>

            {hasMore && (
                <div className="pt-8">
                    <button
                        ref={buttonRef}
                        onClick={() => {
                            if (expanded) {
                                setExpanded(false)
                                requestAnimationFrame(() => {
                                    buttonRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'end',
                                    })
                                })
                            } else {
                                setExpanded(true)
                            }
                        }}
                        className="rounded-[7px] bg-[#ff5c5c] px-5 py-[15px] text-base font-bold tracking-wide text-white uppercase hover:bg-[#e04545]"
                    >
                        {expanded ? 'Hide Reviews' : 'Show More Reviews'}
                    </button>
                </div>
            )}
        </section>
    )
}
